import { Aside } from "./components/Aside";
import {
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
  HTMLAttributes,
  createContext
} from "react";
import { cx } from "shared/utils";
import { MobileNavBar } from "providers/Layout/components/MobileNavBar";
import { Hide, Show } from "shared/components";
import { useMedia } from "shared/providers";
import { useTemplateContext } from "shared/app/VerificationTemplate/provider";
import { Promos } from "providers/Layout/components/Promos";
import { OnboardingChecklist } from "shared/app/OnboardingChecklist";
import { useOnboardingChecklistState } from "shared/app/OnboardingChecklist/hooks";
import { useBoomHelpStore } from "shared/app/BoomHelp/store";

interface Props {
  header?: JSX.Element;
  sidebar?: JSX.Element;
  headerClassName?: string;
  footer?: ReactNode;
  footerClassName?: string;
  sectionClassName?: string;
  layoutClassName?: string;
  children: ReactNode;
  // For scrolling purposes
  sectionIdentifier?: string;
  hideFooterForDesktop?: boolean;
  displayAside?: boolean;
  hideHeaderOnScroll?: boolean;
  sectionProps?: HTMLAttributes<HTMLElement>;
  hideSectionScroll?: boolean;
}

interface LayoutContextValue {
  sectionHeight: number;
  headerHeight: number;
  sectionRef: React.RefObject<HTMLElement> | null;
}

export const LayoutContext = createContext<LayoutContextValue>({
  sectionHeight: 0,
  headerHeight: 0,
  sectionRef: null
});

const getHeaderClassNames = (
  headerClassName: string | undefined,
  hideHeaderOnScroll: boolean | undefined,
  headerVisible: boolean
) =>
  cx(
    headerClassName ||
      "border-b print:border-none border-alpha-100 bg-white py-3",
    hideHeaderOnScroll
      ? `transition-transform duration-200 absolute ${
          // eslint-disable-next-line sonarjs/no-nested-conditional
          headerVisible ? "translate-y-0" : "-translate-y-full"
        }`
      : ""
  );

export const Layout: FC<Props> = ({
  footer,
  sidebar,
  header,
  sectionClassName,
  footerClassName,
  headerClassName,
  layoutClassName,
  children,
  sectionIdentifier,
  hideFooterForDesktop,
  displayAside = true,
  hideHeaderOnScroll,
  sectionProps,
  hideSectionScroll = true
}) => {
  const { isModal } = useTemplateContext();
  const [isOpenFromMobile, setIsOpenFromMobile] = useState(false);
  const boomHelpOpen = useBoomHelpStore((state) => state.isOpen);
  useEffect(() => {
    if (boomHelpOpen) setIsOpenFromMobile(false);
  }, [boomHelpOpen]);
  const headerRef = useRef<HTMLHeadElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [sectionHeight, setSectionHeight] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);

  const { isMobile } = useMedia();
  // Reserve room so the floating checklist never sits on top of the last card.
  const { isAvailable: hasOnboardingChecklist } = useOnboardingChecklistState();

  useEffect(() => {
    const handleResizeEntry = (entry: ResizeObserverEntry) => {
      if (entry.target === (headerRef.current as Element))
        setHeaderHeight(entry.contentRect.height);
      else if (entry.target === (sectionRef.current as Element))
        setSectionHeight(entry.contentRect.height);
    };

    const observer = new ResizeObserver((entries) => {
      entries.forEach(handleResizeEntry);
    });

    if (headerRef.current) observer.observe(headerRef.current);
    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const layoutContextValue: LayoutContextValue = {
    sectionHeight,
    headerHeight,
    sectionRef
  };

  return (
    <LayoutContext.Provider value={layoutContextValue}>
      <div
        className={cx(
          "flex flex-col h-screen overflow-y-hidden print:overflow-y-visible",
          layoutClassName
        )}
      >
        <MobileNavBar setIsOpenFromMobile={setIsOpenFromMobile} />

        {/* Backdrop for mobile aside */}
        <div
          onClick={
            isOpenFromMobile ? () => setIsOpenFromMobile(false) : undefined
          }
          className={cx(
            "fixed inset-0 z-[13] bg-alpha-400 transition-all",
            isOpenFromMobile ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        />
        <div className="flex w-full h-full">
          <Show if={displayAside}>
            <Aside
              isOpenFromMobile={isOpenFromMobile}
              setIsOpenFromMobile={setIsOpenFromMobile}
            />
          </Show>
          <main
            className={cx(
              "max-h-screen min-w-0 flex-grow print:!pl-0 flex pt-16 md:pt-0 overscroll-y-none",
              displayAside ? "" : "lg:pl-0",
              isModal ? "" : "md:pl-20"
            )}
          >
            <Show if={sidebar}>
              <div className="w-auto max-w-full border-r border-alpha-100 bg-white">
                {sidebar}
              </div>
            </Show>
            <div className="relative w-full min-w-0 flex flex-col h-full">
              <Show if={header}>
                <header
                  ref={headerRef}
                  className={getHeaderClassNames(
                    headerClassName,
                    hideHeaderOnScroll,
                    headerVisible
                  )}
                >
                  {header}
                </header>
              </Show>
              <section
                ref={sectionRef}
                onScroll={(e) =>
                  hideHeaderOnScroll &&
                  setHeaderVisible(e.currentTarget.scrollTop === 0)
                }
                className={cx(
                  "flex-grow overflow-y-auto bg-background-900 print:bg-white p-3 md:p-6 print:!h-auto print:overflow-y-visible print:p-0",
                  hideSectionScroll ? "hide-scroll" : "",
                  hasOnboardingChecklist ? "pb-24 print:!pb-0" : "",
                  sectionClassName
                )}
                style={{
                  paddingTop: hideHeaderOnScroll ? headerHeight : undefined
                }}
                id={sectionIdentifier}
                {...sectionProps}
              >
                {children}
              </section>
              <Show if={footer}>
                <Hide if={hideFooterForDesktop && !isMobile}>
                  <footer
                    className={
                      footerClassName ||
                      "border-t border-alpha-100 bg-white print:border-none md:px-6 py-3"
                    }
                  >
                    {footer}
                  </footer>
                </Hide>
              </Show>
            </div>
          </main>
        </div>
      </div>
      <Promos />
      <OnboardingChecklist />
    </LayoutContext.Provider>
  );
};
